import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, combineLatest } from 'rxjs';
import {
  map,
  debounceTime,
  distinctUntilChanged,
  filter
} from 'rxjs/operators';
import { IGeocodingFeature } from '../api/contracts/geocoding.contract';
import { OrderLocationGroupTarget } from '../models/order-location-group.model';
import { GeolocationStore } from './geolocation.store';
import { OrderStore } from './order.store';
import {
  format,
  isWithinInterval,
  parseISO,
  isToday,
  compareAsc,
  isTomorrow,
  differenceInMinutes,
  isFuture
} from 'date-fns';
import { DateTimeUtil } from 'utility/dateTime.util';

interface IOpenTime {
  current: Date;
  start: Date;
  end: Date;
}

@Injectable({
  providedIn: 'root'
})
export class OrderLocationSelectorStore {
  constructor(
    private geolocationStore: GeolocationStore,
    private orderStore: OrderStore
  ) {}

  private readonly _placeNameOptions$ = new BehaviorSubject<
    IGeocodingFeature[]
  >([]);
  readonly placeNameOptions$ = this._placeNameOptions$.asObservable();

  get placeNameOptions(): IGeocodingFeature[] {
    return this._placeNameOptions$.getValue();
  }

  set placeNameOptions(value: IGeocodingFeature[]) {
    this._placeNameOptions$.next(value);
  }

  private readonly _selectedPlaceName$ =
    new BehaviorSubject<IGeocodingFeature | null>(null);
  readonly selectedPlaceName$ = this._selectedPlaceName$.asObservable();

  get selectedPlaceName(): IGeocodingFeature | null {
    return this._selectedPlaceName$.getValue();
  }

  set selectedPlaceName(value: IGeocodingFeature | null) {
    this._selectedPlaceName$.next(value);
  }

  private readonly _selectedPlaceNameChanged$ = new BehaviorSubject<boolean>(
    false
  );
  readonly selectedPlaceNameChanged$ =
    this._selectedPlaceNameChanged$.asObservable();

  get selectedPlaceNameChanged(): boolean {
    return this._selectedPlaceNameChanged$.getValue();
  }

  set selectedPlaceNameChanged(value: boolean) {
    this._selectedPlaceNameChanged$.next(value);
  }

  private readonly _placeNameInput$ = new BehaviorSubject<string>('');
  readonly placeNameInput$ = this._placeNameInput$.asObservable();

  get placeNameInput(): string {
    return this._placeNameInput$.getValue();
  }

  set placeNameInput(value: string) {
    this._placeNameInput$.next(value);
  }

  private readonly _isSearchingCurrentLocation$ = new BehaviorSubject(false);
  readonly isSearchingCurrentLocation$ =
    this._isSearchingCurrentLocation$.asObservable();

  get isSearchingCurrentLocation(): boolean {
    return this._isSearchingCurrentLocation$.getValue();
  }

  set isSearchingCurrentLocation(value: boolean) {
    this._isSearchingCurrentLocation$.next(value);
  }

  private readonly _isSearchingClosestLocations$ = new BehaviorSubject(false);
  readonly isSearchingClosestLocations$ =
    this._isSearchingClosestLocations$.asObservable();

  get isSearchingClosestLocations(): boolean {
    return this._isSearchingClosestLocations$.getValue();
  }

  set isSearchingClosestLocations(value: boolean) {
    this._isSearchingClosestLocations$.next(value);
  }

  readonly placeNameInputContent$: Observable<string> =
    this.placeNameInput$.pipe(filter((value) => value.trim().length > 0));

  readonly distinctPlaceNameInputContent$: Observable<string> =
    this.placeNameInputContent$.pipe(distinctUntilChanged());

  readonly delayedPlaceNameInput$: Observable<string> =
    this.placeNameInputContent$.pipe(
      debounceTime(500),
      distinctUntilChanged((prev, next) => {
        //prevents the searching spinner to display in case there were no changes.
        const equals = prev === next;
        if (equals) {
          this.isSearchingCurrentLocation = false;
        }

        return equals;
      })
    );

  readonly isNavigatorGeolocationEnabled$: Observable<boolean> =
    this.geolocationStore.hasCoordinates$.pipe(
      map((hasCoordinates) => hasCoordinates)
    );

  readonly hasPlaceNameOptions$: Observable<boolean> =
    this.placeNameOptions$.pipe(map((value) => value.length > 0));

  readonly hasPlaceNameInputValue$: Observable<boolean> =
    this.placeNameInput$.pipe(map((value) => value.length > 0));

  readonly shouldShowGeolocationEnabled$: Observable<boolean> = combineLatest([
    this.isNavigatorGeolocationEnabled$,
    this.hasPlaceNameInputValue$
  ]).pipe(
    map(
      ([isNavigatorGeolocationEnabled, hasPlaceNameInputValue]) =>
        isNavigatorGeolocationEnabled && !hasPlaceNameInputValue
    )
  );

  readonly placeNamesFound$: Observable<boolean> = combineLatest([
    this.hasPlaceNameOptions$,
    this.isSearchingCurrentLocation$,
    this.hasPlaceNameInputValue$
  ]).pipe(
    map(
      ([
        hasPlaceNameOptions,
        isSearchingCurrentLocation,
        hasPlaceNameInputValue
      ]) =>
        hasPlaceNameOptions &&
        !isSearchingCurrentLocation &&
        hasPlaceNameInputValue
    )
  );

  readonly placeNotFound$: Observable<boolean> = combineLatest([
    this.placeNamesFound$,
    this.isSearchingCurrentLocation$,
    this.hasPlaceNameInputValue$
  ]).pipe(
    map(
      ([placeNamesFound, isSearchingCurrentLocation, hasPlaceNameInputValue]) =>
        !placeNamesFound &&
        !isSearchingCurrentLocation &&
        hasPlaceNameInputValue
    )
  );

  readonly isAutocompleteOpened$: Observable<boolean> = combineLatest([
    this.selectedPlaceNameChanged$,
    this.isSearchingCurrentLocation$,
    this.placeNamesFound$,
    this.placeNotFound$
  ]).pipe(
    map(
      ([
        selectedPlaceNameChanged,
        isSearchingCurrentLocation,
        placeNamesFound,
        placeNotFound
      ]) =>
        !selectedPlaceNameChanged &&
        (isSearchingCurrentLocation || placeNamesFound || placeNotFound)
    )
  );

  readonly shouldDisplayPermissionDeniedWarning$: Observable<boolean> =
    combineLatest([
      this.geolocationStore.permissionDenied$,
      this.isAutocompleteOpened$
    ]).pipe(
      map(
        ([permissionDenied, isAutocompleteOpened]) =>
          permissionDenied && !isAutocompleteOpened
      )
    );

  readonly searchLocationPlaceHolder$: Observable<string> =
    this.isNavigatorGeolocationEnabled$.pipe(
      map((isNavigatorGeolocationEnabled) =>
        isNavigatorGeolocationEnabled
          ? 'Current Location'
          : 'Find locations near you...'
      )
    );

  readonly isLoadingClosestLocations$: Observable<boolean> = combineLatest([
    this.isSearchingClosestLocations$,
    this.geolocationStore.isLoadingGeolocation$
  ]).pipe(
    map(
      ([isSearchingClosestLocations, isLoadingGeolocation]) =>
        isSearchingClosestLocations || isLoadingGeolocation
    )
  );

  readonly shouldDisplayLocationsList$: Observable<boolean> = combineLatest([
    this.isAutocompleteOpened$,
    this.isSearchingClosestLocations$,
    this.geolocationStore.isLoadingGeolocation$
  ]).pipe(
    map(
      ([
        isAutocompleteOpened,
        isSearchingClosestLocations,
        isLoadingGeolocation
      ]) =>
        !isAutocompleteOpened &&
        !isSearchingClosestLocations &&
        !isLoadingGeolocation
    )
  );

  readonly locationTargets$: Observable<OrderLocationGroupTarget[]> =
    combineLatest([
      this.orderStore.orderLocationGroupTargets$,
      this.orderStore.v2OrderLocationGroupTargets$,
      this.isSearchingClosestLocations$
    ]).pipe(
      map(
        ([
          defaultOrderLocationGroupTargets,
          v2OrderLocationGroupTargets,
          isSearchingClosestLocations
        ]) => {
          return v2OrderLocationGroupTargets.length > 0 ||
            isSearchingClosestLocations
            ? v2OrderLocationGroupTargets
            : defaultOrderLocationGroupTargets;
        }
      )
    );

  readonly locationTargetsWithComputedOrderHours$: Observable<
    OrderLocationGroupTarget[]
  > = this.locationTargets$.pipe(
    map((locationTarget) =>
      locationTarget.map((target) => this.setLocationTargetOrderHours(target))
    )
  );

  private setLocationTargetOrderHours(
    target: OrderLocationGroupTarget
  ): OrderLocationGroupTarget {
    const { openStartTime, openEndTime } = target;

    if (!!openStartTime && !!openEndTime) {
      const nextOpenTime: IOpenTime = {
        current: new Date(),
        start: parseISO(openStartTime),
        end: parseISO(openEndTime)
      };

      if (this.areOrderHoursAvailable(nextOpenTime)) {
        target.hoursInstructions = this.getLocationHours(nextOpenTime);
        target.locationOpened = this.isTargetOpenedNow(nextOpenTime);
      }
    }
    return target;
  }

  getLocationHours(openTime: IOpenTime): string {
    if (DateTimeUtil.isOpenToday(openTime)) {
      return `Pick-up hours today ${DateTimeUtil.formatDefaultTime(openTime)}`;
    } else {
      if (DateTimeUtil.isFutureDate(openTime.start)) {
        if (DateTimeUtil.isOpenTomorrow(openTime)) {
          return `Pick-up hours tomorrow ${DateTimeUtil.formatDefaultTime(
            openTime
          )}`;
        } else {
          return `Pick-up hours ${DateTimeUtil.formatTimeWithStartTimeWithDayName(
            openTime
          )}`;
        }
      }
    }

    return '';
  }
  private areOrderHoursAvailable(openTime: IOpenTime): boolean {
    const { start, end } = openTime;
    return differenceInMinutes(end, start) > 0;
  }

  private isTargetOpenedNow(openTime: IOpenTime): boolean {
    const { current, start, end } = openTime;
    return isWithinInterval(current, {
      start: start,
      end: end
    });
  }
}
